/**
 * Aura Viva tier picker + cart add (Esencia / Infinita)
 *
 * Rules:
 * - Esencia: intercept form, add jewel via fetch, show branded popup.
 * - Infinita: intercept form, add jewel + upgrade product via fetch, show branded popup.
 *
 * Both tiers show a custom on-brand popup with:
 *   - Product name + plan name
 *   - "Ver carrito" → localised cart URL
 *   - "Seguir comprando" → dismiss
 *
 * Config (AV_TIER_CFG_<sectionId>):
 *   { "infinita": { "variant_id": "<shopify variant id>" } }
 */
(function () {
  "use strict";

  if (window.__AV_TIER_JS_LOADED__) return;
  window.__AV_TIER_JS_LOADED__ = true;

  /* ---- DOM helpers ---- */

  function $all(root, sel) {
    return Array.from((root || document).querySelectorAll(sel));
  }

  function parseJsonScriptById(id) {
    const el = document.getElementById(id);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent || "{}");
    } catch (e) {
      return null;
    }
  }

  function getTierConfigForSection(sectionId) {
    return parseJsonScriptById("AV_TIER_CFG_" + sectionId);
  }

  function getI18nForSection(sectionId) {
    return parseJsonScriptById("AV_TIER_I18N_" + sectionId) || {};
  }

  /* ---- i18n ---- */

  function fmt(str, vars) {
    if (!str) return "";
    if (!vars) return String(str);
    return String(str).replace(/\{([a-zA-Z0-9_]+)\}/g, function (_, k) {
      return Object.prototype.hasOwnProperty.call(vars, k) ? String(vars[k]) : "";
    });
  }

  function t(i18n, key, vars, fallback) {
    const template = i18n && i18n[key] ? i18n[key] : fallback || "";
    return fmt(template, vars);
  }

  /* ---- tier state ---- */

  function normalizeTier(tier) {
    if (!tier) return "esencia";
    tier = String(tier).toLowerCase();
    if (tier === "infinita") return "infinita";
    return "esencia";
  }

  function setTier(form, tier) {
    const tierInput = form.querySelector("[data-av-tier]");
    if (tierInput) tierInput.value = tier;
  }

  function setTierButtonsState(picker, tier) {
    const btns = $all(picker, "[data-av-tier-btn]");
    btns.forEach(function (b) {
      const isActive = b.getAttribute("data-av-tier-btn") === tier;
      b.setAttribute("aria-pressed", isActive ? "true" : "false");
      if (isActive) b.classList.add("is-active");
      else b.classList.remove("is-active");
    });
  }

  /* ---- config validation ---- */

  function ensureConfigForTier(cfg, tier, i18n) {
    if (!cfg) return { ok: false, reason: t(i18n, "err_missing_config", null, "Missing tier config JSON in the page.") };
    if (tier === "esencia") return { ok: true };

    var entry = cfg[tier];
    if (!entry || !entry.variant_id) {
      return {
        ok: false,
        reason: t(i18n, "err_missing_upgrade_variant", { tier: tier }, "Missing upgrade variant for {tier}."),
      };
    }

    return { ok: true };
  }

  /* ---- form helpers ---- */

  function serializeFormProperties(form) {
    var props = {};
    var inputs = $all(
      form,
      'input[name^="properties["], textarea[name^="properties["], select[name^="properties["]'
    );
    inputs.forEach(function (el) {
      if (el.disabled) return;
      var name = el.getAttribute("name");
      var m = name && name.match(/^properties\[(.+)\]$/);
      if (!m) return;
      var key = m[1];
      if (el.type === "checkbox") {
        if (el.checked) props[key] = el.value || "true";
        return;
      }
      props[key] = el.value;
    });
    return props;
  }

  function getSelectedVariantId(form) {
    var idEl = form.querySelector('input[name="id"]');
    return idEl && idEl.value ? String(idEl.value) : null;
  }

  function disableSubmit(form, disabled) {
    var btn = form.querySelector('button[name="add"], button[type="submit"]');
    if (!btn) return;
    btn.disabled = !!disabled;
    btn.setAttribute("aria-busy", disabled ? "true" : "false");
  }

  function getSectionIdFromForm(form) {
    var pf = form.closest("product-form");
    return pf ? pf.getAttribute("data-section-id") : null;
  }

  function getProductTitleFromForm(form) {
    var picker = form.querySelector("[data-av-tier-picker]");
    return picker ? (picker.getAttribute("data-product-title") || "") : "";
  }

  function getPriceFromForm(form) {
    var picker = form.querySelector("[data-av-tier-picker]");
    return picker ? picker.getAttribute("data-product-price") || "" : "";
  }

  function getCartUrlFromForm(form) {
    var picker = form.querySelector("[data-av-tier-picker]");
    if (picker && picker.getAttribute("data-cart-url")) {
      return picker.getAttribute("data-cart-url");
    }
    return "/cart";
  }

  /* ---- uid ---- */

  function uuidv4() {
    if (window.crypto && crypto.randomUUID) return crypto.randomUUID();
    var buf = new Uint8Array(16);
    (window.crypto || window.msCrypto).getRandomValues(buf);
    buf[6] = (buf[6] & 0x0f) | 0x40;
    buf[8] = (buf[8] & 0x3f) | 0x80;
    var b = Array.from(buf, function (x) {
      return x.toString(16).padStart(2, "0");
    }).join("");
    return (
      b.slice(0, 8) + "-" +
      b.slice(8, 12) + "-" +
      b.slice(12, 16) + "-" +
      b.slice(16, 20) + "-" +
      b.slice(20)
    );
  }

  function ensureUid(props) {
    if (!props) props = {};
    // Always generate a fresh uid on every add.
    // Reusing a uid causes Shopify to merge line items → shared gallery slot.
    props._av_uid = uuidv4();
    return props;
  }

  // Unique-per-line-item id. Prevents Shopify from merging same-variant
  // line items that happen to share other properties (e.g. two of the same
  // jewel SKU linked into one gallery group). Backend ignores this — it's
  // purely a Shopify cart de-merge mechanism.
  function makeLineId() {
    return uuidv4();
  }

  /* ---- fetch helpers ---- */

  async function fetchJson(url, opts) {
    var res = await fetch(
      url,
      Object.assign(
        { headers: { "Content-Type": "application/json", Accept: "application/json" } },
        opts
      )
    );
    var data = await res.json().catch(function () { return {}; });
    if (!res.ok) {
      var msg = (data && (data.description || data.message)) || "Request failed: " + res.status;
      var err = new Error(msg);
      err.status = res.status;
      err.data = data;
      throw err;
    }
    return data;
  }

  async function addItemsToCart(payload) {
    return fetchJson("/cart/add.js", {
      method: "POST",
      body: JSON.stringify(payload),
    });
  }

  /* ---- branded popup ---- */

  function injectPopupStyles() {
    if (document.getElementById("av-popup-styles")) return;
    var style = document.createElement("style");
    style.id = "av-popup-styles";
    style.textContent = [
      "@import url('https://fonts.googleapis.com/css2?family=Quattrocento+Sans:wght@400;700&family=EB+Garamond:wght@400;600&display=swap');",

      "#av-popup {",
        "position: fixed;",
        "bottom: 0;",
        "left: 0;",
        "right: 0;",
        "z-index: 999999;",
        "background: #EFE0C8;",
        "border-top: 1.5px solid rgba(44,51,47,.22);",
        "border-radius: 16px 16px 0 0;",
        "padding: 24px 20px 36px;",
        "box-shadow: 0 -8px 40px rgba(44,51,47,.2);",
        "transform: translateY(100%);",
        "transition: transform .32s cubic-bezier(.32,.72,0,1);",
        "max-width: 480px;",
        "margin: 0 auto;",
        "font-family: 'Quattrocento Sans', sans-serif;",
        "box-sizing: border-box;",
      "}",

      "#av-popup.av-is-visible {",
        "transform: translateY(0);",
      "}",

      "#av-popup-close {",
        "position: absolute;",
        "top: 14px;",
        "right: 16px;",
        "width: 28px;",
        "height: 28px;",
        "display: flex;",
        "align-items: center;",
        "justify-content: center;",
        "background: transparent;",
        "border: none;",
        "cursor: pointer;",
        "color: rgba(44,51,47,.5);",
        "padding: 0;",
        "line-height: 1;",
        "font-size: 20px;",
      "}",

      "#av-popup-close:hover { color: #2C332F; }",

      "#av-popup-check {",
        "display: flex;",
        "align-items: center;",
        "gap: 7px;",
        "font-size: 10px;",
        "font-weight: 700;",
        "letter-spacing: .14em;",
        "text-transform: uppercase;",
        "color: #2C332F;",
        "margin-bottom: 14px;",
        "padding-right: 32px;",
      "}",

      "#av-popup-product {",
        "font-family: 'EB Garamond', serif;",
        "font-size: 19px;",
        "font-weight: 600;",
        "color: #1D1D1F;",
        "line-height: 1.3;",
        "margin-bottom: 22px;",
      "}",

      "#av-popup-plan {",
        "display: inline-block;",
        "margin-top: 5px;",
        "font-family: 'Quattrocento Sans', sans-serif;",
        "font-size: 11px;",
        "font-weight: 700;",
        "color: #2C332F;",
        "background: rgba(44,51,47,.1);",
        "padding: 3px 9px;",
        "border-radius: 20px;",
        "letter-spacing: .06em;",
        "text-transform: uppercase;",
      "}",

      "#av-popup-cart {",
        "display: block;",
        "width: 100%;",
        "padding: 15px;",
        "background: #2C332F;",
        "color: #EFE0C8;",
        "border: none;",
        "border-radius: 9px;",
        "font-family: 'Quattrocento Sans', sans-serif;",
        "font-size: 14px;",
        "font-weight: 700;",
        "letter-spacing: .04em;",
        "cursor: pointer;",
        "margin-bottom: 10px;",
        "text-align: center;",
        "text-decoration: none;",
        "box-sizing: border-box;",
      "}",

      "#av-popup-price {",
        "display: block;",
        "margin-top: 6px;",
        "font-family: 'Quattrocento Sans', sans-serif;",
        "font-size: 15px;",
        "font-weight: 700;",
        "color: #2C332F;",
      "}",

      "#av-popup-viewcart {",
        "display: block;",
        "width: 100%;",
        "padding: 13px;",
        "background: transparent;",
        "color: #2C332F;",
        "border: 1.5px solid #2C332F;",
        "border-radius: 9px;",
        "font-family: 'Quattrocento Sans', sans-serif;",
        "font-size: 14px;",
        "font-weight: 700;",
        "letter-spacing: .04em;",
        "margin-bottom: 4px;",
        "text-align: center;",
        "text-decoration: none;",
        "box-sizing: border-box;",
      "}",

      "#av-popup { max-height: 88vh; overflow-y: auto; }",
      "#av-popup-q-title {",
        "font-family: 'EB Garamond', serif;",
        "font-size: 18px;",
        "font-weight: 600;",
        "color: #1D1D1F;",
        "line-height: 1.3;",
        "margin: 0 0 4px;",
      "}",
      "#av-popup-q-sub {",
        "font-size: 13px;",
        "color: #5a5a5c;",
        "line-height: 1.45;",
        "margin: 0 0 14px;",
      "}",
      ".av-popup-opt {",
        "display: flex;",
        "align-items: center;",
        "justify-content: center;",
        "gap: 10px;",
        "width: 100%;",
        "padding: 13px 14px;",
        "margin: 0 0 9px;",
        "background: #FAF6EF;",
        "color: #2C332F;",
        "border: 1.5px solid #2C332F;",
        "border-radius: 9px;",
        "font-family: 'Quattrocento Sans', sans-serif;",
        "font-size: 14px;",
        "font-weight: 700;",
        "cursor: pointer;",
        "text-align: center;",
        "box-sizing: border-box;",
      "}",
      ".av-popup-opt:disabled { opacity: .55; cursor: default; }",
      ".av-popup-opt__thumbs { display: inline-flex; flex: none; }",
      ".av-popup-opt__thumbs img {",
        "width: 30px;",
        "height: 30px;",
        "border-radius: 50%;",
        "object-fit: cover;",
        "background: #fff;",
        "border: 1.5px solid #FAF6EF;",
        "margin-left: -8px;",
      "}",
      ".av-popup-opt__thumbs img:first-child { margin-left: 0; }",
      "#av-popup-status {",
        "display: flex;",
        "align-items: center;",
        "gap: 7px;",
        "font-size: 13px;",
        "font-weight: 700;",
        "color: #2C332F;",
        "margin: 0 0 14px;",
      "}",
      "#av-popup-status::before { content: '\\2726'; color: #C79A3F; }",

      "#av-popup-continue {",
        "display: block;",
        "width: 100%;",
        "padding: 10px;",
        "background: transparent;",
        "border: none;",
        "font-family: 'Quattrocento Sans', sans-serif;",
        "font-size: 13px;",
        "color: #7A6E60;",
        "cursor: pointer;",
        "text-align: center;",
        "text-decoration: underline;",
        "text-underline-offset: 3px;",
      "}",
    ].join("");
    document.head.appendChild(style);
  }

  var popupDismissTimer = null;

  function escapeHtml(s) {
    return String(s == null ? "" : s).replace(/[&<>"']/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
    });
  }

  // Keep the header cart count in step with background cart changes.
  function refreshCartBubble() {
    var bubble = document.getElementById("cart-icon-bubble");
    if (!bubble) return;
    fetch(window.location.pathname + "?sections=cart-icon-bubble")
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var html = data && data["cart-icon-bubble"];
        if (!html) return;
        var doc = new DOMParser().parseFromString(html, "text/html");
        var inner = doc.querySelector(".shopify-section");
        if (inner) bubble.innerHTML = inner.innerHTML;
      })
      .catch(function () {});
  }

  function shortName(title) {
    return String(title || "").split(" · ")[0].trim();
  }

  function thumbUrl(src) {
    if (!src) return "";
    return src + (src.indexOf("?") === -1 ? "?" : "&") + "width=96";
  }

  function joinNames(names, i18n) {
    var and = t(i18n, "q_and", null, " y ");
    if (names.length <= 1) return names[0] || "";
    return names.slice(0, -1).join(", ") + and + names[names.length - 1];
  }

  /**
   * After a jewel is added: if the cart already holds other jewels, build the
   * "which gallery does it open?" question. Returns null for a first jewel.
   */
  async function buildGalleryQuestion(newUid, productTitle, i18n) {
    var link = window.AVCartLink;
    if (!link || !link.mergeInto) return null;
    var cart = await link.fetchCart();
    var items = cart.items || [];
    var newJewel = items.find(function (it) {
      var p = it.properties || {};
      return p._av_uid === newUid && String(p._av_upgrade || "") !== "true";
    });
    if (!newJewel) return null;

    // Other galleries in the cart, oldest first.
    var groups = [];
    var byUid = {};
    for (var i = items.length - 1; i >= 0; i--) {
      var it = items[i];
      var p = it.properties || {};
      var uid = p._av_uid || "";
      if (!uid || uid === newUid || String(p._av_upgrade || "") === "true") continue;
      if (!byUid[uid]) { byUid[uid] = { uid: uid, names: [], thumbs: [] }; groups.push(byUid[uid]); }
      var n = shortName(it.product_title);
      for (var q = 0; q < it.quantity; q++) {
        byUid[uid].names.push(n);
        if (it.image) byUid[uid].thumbs.push(thumbUrl(it.image));
      }
    }
    if (!groups.length) return null;

    var done = t(i18n, "q_done_merged", null, "Comparten galería");
    var apart = t(i18n, "q_done_separate", null, "Cada una con su galería");
    function mergeOpt(g, label) {
      return {
        label: label,
        thumbs: g.thumbs,
        targetUid: g.uid,
        run: async function () {
          await link.mergeInto(newJewel.key, newUid, g.uid);
          document.dispatchEvent(new CustomEvent("av:cart-changed"));
          refreshCartBubble();
          return done;
        },
      };
    }
    function apartOpt(label) {
      return { label: label, run: async function () { return apart; } };
    }

    var mine = shortName(productTitle);
    if (groups.length === 1) {
      var g = groups[0];
      if (g.names.length === 1) {
        var key = g.names[0] === mine ? "q_one_same" : "q_one";
        return {
          title: t(i18n, key, { name: g.names[0] }, "¿Comparte galería con tu {name}?"),
          sub: t(i18n, "q_one_sub", null, ""),
          options: [mergeOpt(g, t(i18n, "q_yes", null, "Sí, misma galería")), apartOpt(t(i18n, "q_no_one", null, "No, una para cada una"))],
        };
      }
      return {
        title: t(i18n, "q_group", { names: joinNames(g.names, i18n) }, "¿La añadimos a la galería de {names}?"),
        sub: t(i18n, "q_group_sub", null, ""),
        options: [mergeOpt(g, t(i18n, "q_yes_short", null, "Sí")), apartOpt(t(i18n, "q_no_own", null, "No, galería propia"))],
      };
    }
    var opts = groups.map(function (gr) {
      return mergeOpt(gr, t(i18n, "q_multi_opt", { names: joinNames(gr.names, i18n) }, "La de {names}"));
    });
    opts.push(apartOpt(t(i18n, "q_multi_new", null, "Una nueva")));
    return { title: t(i18n, "q_multi", null, "¿Qué galería abre esta joya?"), sub: "", options: opts };
  }

  async function showPopupAfterAdd(productTitle, planLabel, i18n, cartUrl, priceText, newUid) {
    var question = null;
    try { question = await buildGalleryQuestion(newUid, productTitle, i18n); } catch (e) { question = null; }
    refreshCartBubble();
    showPopup(productTitle, planLabel, i18n, cartUrl, priceText, question);
  }

  function showPopup(productTitle, planLabel, i18n, cartUrl, priceText, question) {
    cartUrl = cartUrl || "/cart";
    // Straight to checkout: Shopify's /checkout redirects to the current cart's checkout.
    var checkoutUrl = cartUrl.replace(/\/cart\/?$/, "/checkout");
    injectPopupStyles();

    // Remove any existing popup cleanly
    var existing = document.getElementById("av-popup");
    if (existing) existing.remove();
    if (popupDismissTimer) {
      clearTimeout(popupDismissTimer);
      popupDismissTimer = null;
    }

    var addedText    = t(i18n, "popup_added",    null, "Artículo agregado a tu carrito");
    var cartText     = t(i18n, "popup_cart",     null, "Ver carrito");
    var continueText = t(i18n, "popup_continue", null, "Seguir comprando");
    var checkoutText = t(i18n, "popup_checkout", null, "Finalizar compra");

    var popup = document.createElement("div");
    popup.id = "av-popup";

    // Check icon SVG
    var checkSvg = '<svg width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg" style="flex-shrink:0">'
      + '<circle cx="6.5" cy="6.5" r="6" stroke="#2C332F"/>'
      + '<path d="M3.5 6.5l2 2 4-4" stroke="#2C332F" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/>'
      + '</svg>';

    popup.innerHTML = [
      '<button type="button" id="av-popup-close" aria-label="Cerrar">&times;</button>',
      '<div id="av-popup-check">' + checkSvg + '<span>' + addedText + '</span></div>',
      '<div id="av-popup-product">',
        productTitle,
        (priceText ? '<span id="av-popup-price">' + priceText + '</span>' : ''),
        '<span id="av-popup-plan">' + planLabel + '</span>',
      '</div>',
      '<div id="av-popup-body"></div>',
    ].join("");

    var actionsHtml = [
      '<a href="' + checkoutUrl + '" id="av-popup-cart">' + checkoutText + '</a>',
      '<a href="' + cartUrl + '" id="av-popup-viewcart">' + cartText + '</a>',
      '<button type="button" id="av-popup-continue">' + continueText + '</button>',
    ].join("");

    var body = popup.querySelector("#av-popup-body");

    function showActions(statusText) {
      body.innerHTML = (statusText ? '<div id="av-popup-status">' + escapeHtml(statusText) + '</div>' : '') + actionsHtml;
      body.querySelector("#av-popup-continue").addEventListener("click", function () {
        dismissPopup();
      });
    }

    // Multi-jewel cart: ask once which gallery this jewel opens. Checkout
    // appears after the answer; closing the sheet keeps its own gallery.
    if (question && question.options && question.options.length) {
      var optsHtml = question.options.map(function (o, i) {
        var thumbs = (o.thumbs || []).slice(0, 3).map(function (src) {
          return '<img src="' + escapeHtml(src) + '" alt="" loading="lazy">';
        }).join("");
        return '<button type="button" class="av-popup-opt" data-opt="' + i + '">'
          + (thumbs ? '<span class="av-popup-opt__thumbs">' + thumbs + '</span>' : '')
          + '<span>' + escapeHtml(o.label) + '</span></button>';
      }).join("");
      body.innerHTML = '<p id="av-popup-q-title">' + escapeHtml(question.title) + '</p>'
        + (question.sub ? '<p id="av-popup-q-sub">' + escapeHtml(question.sub) + '</p>' : '')
        + optsHtml;
      Array.prototype.forEach.call(body.querySelectorAll(".av-popup-opt"), function (btn) {
        btn.addEventListener("click", async function () {
          var o = question.options[Number(btn.getAttribute("data-opt"))];
          Array.prototype.forEach.call(body.querySelectorAll(".av-popup-opt"), function (b) { b.disabled = true; });
          if (o.targetUid) btn.lastChild.textContent = t(i18n, "q_working", null, "Un momento…");
          var status;
          try {
            status = await o.run();
          } catch (err) {
            console.error("av-tier.js: merge failed", err);
            status = t(i18n, "q_error", null, "No se pudieron unir. Puedes hacerlo en el carrito.");
          }
          showActions(status);
        });
      });
    } else {
      showActions("");
    }

    document.body.appendChild(popup);

    // Trigger slide-in on next frame
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        popup.classList.add("av-is-visible");
      });
    });

    // Close button
    popup.querySelector("#av-popup-close").addEventListener("click", function () {
      dismissPopup();
    });

    // No auto-dismiss: the buyer's next step (checkout) lives in this sheet.
  }

  function dismissPopup() {
    var popup = document.getElementById("av-popup");
    if (!popup) return;
    popup.classList.remove("av-is-visible");
    setTimeout(function () {
      if (popup.parentNode) popup.parentNode.removeChild(popup);
    }, 340);
    if (popupDismissTimer) {
      clearTimeout(popupDismissTimer);
      popupDismissTimer = null;
    }
  }

  /* ---- Esencia upsell modal ---- */

  function injectUpsellStyles() {
    if (document.getElementById("av-upsell-styles")) return;
    var style = document.createElement("style");
    style.id = "av-upsell-styles";
    style.textContent = [
      "@import url('https://fonts.googleapis.com/css2?family=Quattrocento+Sans:wght@400;700&family=EB+Garamond:wght@400;600&display=swap');",
      "#av-upsell-overlay{position:fixed;inset:0;z-index:1000000;background:rgba(44,51,47,.55);display:flex;align-items:center;justify-content:center;padding:20px;box-sizing:border-box;opacity:0;transition:opacity .25s ease;overflow-y:auto;}",
      "#av-upsell-overlay.av-is-visible{opacity:1;}",
      "#av-upsell-card{width:380px;max-width:100%;background:#F7F1E6;border-radius:18px;box-shadow:0 20px 60px rgba(0,0,0,.35);font-family:'Quattrocento Sans',sans-serif;color:#2C332F;position:relative;margin:auto;box-sizing:border-box;transform:translateY(14px) scale(.98);transition:transform .28s cubic-bezier(.32,.72,0,1);}",
      "#av-upsell-overlay.av-is-visible #av-upsell-card{transform:none;}",
      "#av-upsell-inner{padding:26px 22px 22px;}",
      "#av-upsell-close{position:absolute;top:12px;right:12px;width:30px;height:30px;border:none;background:rgba(44,51,47,.06);border-radius:50%;color:#2C332F;cursor:pointer;display:flex;align-items:center;justify-content:center;font-size:18px;line-height:1;padding:0;}",
      "#av-upsell-close:hover{background:rgba(44,51,47,.12);}",
      ".av-upsell-badge{display:inline-flex;align-items:center;gap:6px;background:rgba(44,51,47,.06);color:#7A6E60;font-size:10px;font-weight:700;letter-spacing:.12em;text-transform:uppercase;padding:4px 10px;border-radius:20px;margin-bottom:14px;}",
      ".av-upsell-title{font-family:'EB Garamond',serif;font-size:22px;font-weight:600;line-height:1.2;margin:0 0 8px;color:#2C332F;}",
      ".av-upsell-intro{font-size:13px;color:#5a5a5c;line-height:1.55;margin:0 0 16px;}",
      ".av-upsell-intro strong{color:#2C332F;}",
      ".av-upsell-miss{display:flex;flex-direction:column;gap:7px;margin-bottom:18px;}",
      ".av-upsell-miss-row{display:flex;align-items:center;gap:9px;font-size:12.5px;color:#9a9a9a;}",
      ".av-upsell-x{color:#c0392b;font-weight:700;flex-shrink:0;font-size:12px;line-height:1;}",
      ".av-upsell-link{display:flex;gap:7px;align-items:flex-start;font-size:11.5px;color:#7A6E60;line-height:1.5;margin:0 0 12px;padding:0 2px;}",
      ".av-upsell-link svg{flex-shrink:0;margin-top:1px;}",
      ".av-upsell-unlock{background:#EFE0C8;border:1.5px solid #2C332F;border-radius:12px;padding:14px 16px;margin-bottom:18px;}",
      ".av-upsell-unlock-title{display:flex;align-items:center;gap:8px;font-family:'EB Garamond',serif;font-size:16px;font-weight:600;color:#2C332F;}",
      ".av-upsell-unlock-note{font-size:11.5px;color:#7A6E60;margin:6px 0 0;line-height:1.5;}",
      "#av-upsell-upgrade{width:100%;background:#2C332F;color:#EFE0C8;border:none;border-radius:10px;padding:14px;font-family:'Quattrocento Sans',sans-serif;font-size:14px;font-weight:700;cursor:pointer;display:flex;align-items:center;justify-content:center;gap:8px;margin-bottom:10px;box-sizing:border-box;animation:av-upsell-glow 2.2s ease-in-out infinite;}",
      "#av-upsell-upgrade:hover{background:#242a26;animation:none;box-shadow:0 4px 22px rgba(44,51,47,.45);}",
      "@keyframes av-upsell-glow{0%,100%{box-shadow:0 2px 8px rgba(44,51,47,.15);}50%{box-shadow:0 4px 22px rgba(44,51,47,.5);}}",
      "@media (prefers-reduced-motion:reduce){#av-upsell-upgrade{animation:none;box-shadow:0 2px 8px rgba(44,51,47,.15);}}",
      "#av-upsell-continue{width:100%;background:transparent;color:#7A6E60;border:1px solid rgba(44,51,47,.22);border-radius:10px;padding:12px;font-family:'Quattrocento Sans',sans-serif;font-size:13px;cursor:pointer;box-sizing:border-box;}",
      "#av-upsell-continue:hover{border-color:rgba(44,51,47,.4);color:#2C332F;}",
    ].join("");
    document.head.appendChild(style);
  }

  var upsellKeyHandler = null;

  function closeUpsell(cb) {
    var overlay = document.getElementById("av-upsell-overlay");
    if (!overlay) {
      if (typeof cb === "function") cb();
      return;
    }
    overlay.classList.remove("av-is-visible");
    document.body.style.overflow = "";
    if (upsellKeyHandler) {
      document.removeEventListener("keydown", upsellKeyHandler);
      upsellKeyHandler = null;
    }
    setTimeout(function () {
      if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
      if (typeof cb === "function") cb();
    }, 260);
  }

  function showEsenciaUpsell(opts) {
    opts = opts || {};
    var i18n = opts.i18n || {};
    injectUpsellStyles();

    var existing = document.getElementById("av-upsell-overlay");
    if (existing) existing.remove();

    var linkSvg = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M9 15l6-6"/><path d="M11 6l1-1a4 4 0 0 1 6 6l-1 1"/><path d="M13 18l-1 1a4 4 0 0 1-6-6l1-1"/></svg>';
    var sparkSvg = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M12 4l1.8 4.7L18 10.5l-4.2 1.8L12 17l-1.8-4.7L6 10.5l4.2-1.8z"/></svg>';
    var upSvg = '<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 16V9M9 12l3-3 3 3"/></svg>';

    var badge       = t(i18n, "upsell_badge", null, "Before you add to cart");
    var title       = t(i18n, "upsell_title", null, "You picked Esencia, the free gallery");
    var intro       = t(i18n, "upsell_intro", null, "Esencia holds just <strong>75 photos</strong> and none of the extras.");
    var miss = [
      t(i18n, "upsell_miss1", null, "925 more photos (1,000 total)"),
      t(i18n, "upsell_miss2", null, "50 videos + voice notes"),
      t(i18n, "upsell_miss3", null, "Memory timelines"),
      t(i18n, "upsell_miss4", null, "Family editors and Live Mode"),
    ];
    var linkLine    = t(i18n, "upsell_link", null, "Buying more than one jewel? A single Infinita plan can be shared across several. Just link your jewels together in the cart.");
    var unlockTitle = t(i18n, "upsell_unlock_title", null, "Unlock everything with Infinita");
    var unlockNote  = t(i18n, "upsell_unlock_note", null, "+€34.99 · one payment, yours forever. No subscription.");
    var btnUpgrade  = t(i18n, "upsell_btn_upgrade", null, "Add Infinita instead");
    var btnContinue = t(i18n, "upsell_btn_continue", null, "No thanks, continue with Esencia");

    var missRows = miss.map(function (m) {
      return '<div class="av-upsell-miss-row"><span class="av-upsell-x">&#10007;</span><span>' + m + "</span></div>";
    }).join("");

    var overlay = document.createElement("div");
    overlay.id = "av-upsell-overlay";
    overlay.setAttribute("role", "dialog");
    overlay.setAttribute("aria-modal", "true");
    overlay.innerHTML = [
      '<div id="av-upsell-card">',
        '<button type="button" id="av-upsell-close" aria-label="Close">&times;</button>',
        '<div id="av-upsell-inner">',
          '<div class="av-upsell-badge">' + badge + "</div>",
          '<h3 class="av-upsell-title">' + title + "</h3>",
          '<p class="av-upsell-intro">' + intro + "</p>",
          '<div class="av-upsell-miss">' + missRows + "</div>",
          '<p class="av-upsell-link">' + linkSvg + "<span>" + linkLine + "</span></p>",
          '<div class="av-upsell-unlock">',
            '<div class="av-upsell-unlock-title">' + sparkSvg + "<span>" + unlockTitle + "</span></div>",
            '<p class="av-upsell-unlock-note">' + unlockNote + "</p>",
          "</div>",
          '<button type="button" id="av-upsell-upgrade">' + upSvg + "<span>" + btnUpgrade + "</span></button>",
          '<button type="button" id="av-upsell-continue">' + btnContinue + "</button>",
        "</div>",
      "</div>",
    ].join("");

    document.body.appendChild(overlay);
    document.body.style.overflow = "hidden";

    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        overlay.classList.add("av-is-visible");
      });
    });

    overlay.querySelector("#av-upsell-close").addEventListener("click", function () {
      closeUpsell(opts.onClose);
    });
    overlay.querySelector("#av-upsell-continue").addEventListener("click", function () {
      closeUpsell(opts.onContinue);
    });
    overlay.querySelector("#av-upsell-upgrade").addEventListener("click", function () {
      closeUpsell(opts.onUpgrade);
    });
    overlay.addEventListener("click", function (e) {
      if (e.target === overlay) closeUpsell(opts.onClose);
    });
    upsellKeyHandler = function (e) {
      if (e.key === "Escape" || e.keyCode === 27) closeUpsell(opts.onClose);
    };
    document.addEventListener("keydown", upsellKeyHandler);

    var up = overlay.querySelector("#av-upsell-upgrade");
    if (up) up.focus();
  }

  /* ---- tier picker init ---- */

  function onTierPickerInit(form) {
    var sectionId = getSectionIdFromForm(form);
    if (!sectionId) return;
    var picker = form.querySelector("[data-av-tier-picker]");
    if (!picker) return;

    var tierInput = form.querySelector("[data-av-tier]");
    var currentRaw = tierInput ? String(tierInput.value || "").toLowerCase() : "";
    // No plan is pre-selected: only reflect a selected state if one is chosen.
    if (currentRaw === "esencia" || currentRaw === "infinita") {
      setTierButtonsState(picker, currentRaw);
    }

    $all(picker, "[data-av-tier-btn]").forEach(function (btn) {
      btn.addEventListener("click", function () {
        var tSel = normalizeTier(btn.getAttribute("data-av-tier-btn"));
        setTier(form, tSel);
        setTierButtonsState(picker, tSel);
        var err = form.querySelector("[data-av-tier-error]");
        if (err) err.hidden = true;
        picker.classList.remove("avpp--error");
      });
    });
  }

  /* ---- upsell suppression (per shopping session) ---- */
  // Once the shopper has resolved the upsell once (chose Infinita or chose to
  // continue with Esencia), don't show it again this session — so buying a
  // linked set doesn't trigger the popup on every jewel. Resets next visit.

  var AV_UPSELL_KEY = "av_upsell_resolved";

  function upsellResolved() {
    try {
      return window.sessionStorage.getItem(AV_UPSELL_KEY) === "1";
    } catch (e) {
      return false;
    }
  }

  function markUpsellResolved() {
    try {
      window.sessionStorage.setItem(AV_UPSELL_KEY, "1");
    } catch (e) {}
  }

  /* ---- require an explicit plan choice ---- */

  function showChoosePlan(form) {
    var picker = form.querySelector("[data-av-tier-picker]");
    var err = form.querySelector("[data-av-tier-error]");
    if (err) {
      err.hidden = false;
      // Restart the shake animation on every attempt.
      err.style.animation = "none";
      void err.offsetWidth;
      err.style.animation = "";
    }
    if (picker) {
      picker.classList.add("avpp--error");
      try { picker.scrollIntoView({ behavior: "smooth", block: "center" }); } catch (e) {}
    }
  }

  /* ---- submit handler ---- */

  async function onSubmit(e) {
    var form = e.target;
    if (!form || form.getAttribute("data-type") !== "add-to-cart-form") return;

    var tierEl = form.querySelector("[data-av-tier]");
    if (!tierEl) return;
    var rawTier = String(tierEl.value || "").toLowerCase();

    // Intercept for the branded flow / to require a plan choice
    e.preventDefault();
    e.stopPropagation();
    if (typeof e.stopImmediatePropagation === "function") e.stopImmediatePropagation();

    // No plan is pre-selected — the shopper must pick Esencia or Infinita.
    if (rawTier !== "esencia" && rawTier !== "infinita") {
      showChoosePlan(form);
      return;
    }
    var tier = normalizeTier(rawTier);

    var sectionId    = getSectionIdFromForm(form);
    var i18n         = getI18nForSection(sectionId);
    var cfg          = getTierConfigForSection(sectionId);
    var productTitle = getProductTitleFromForm(form);
    var cartUrl      = getCartUrlFromForm(form);
    var priceText    = getPriceFromForm(form);

    var jewelVariantId = getSelectedVariantId(form);
    if (!jewelVariantId) {
      alert(t(i18n, "err_missing_variant", null, "Missing product variant id."));
      return;
    }

    // Each physical necklace must be a separate cart add to get its own _av_uid.
    function resetQty() {
      var qtyInput = form.querySelector('input[name="quantity"]');
      if (qtyInput && parseInt(qtyInput.value, 10) > 1) qtyInput.value = "1";
    }

    function clearUid() {
      var uidInput = form.querySelector("[data-av-uid]");
      if (uidInput) uidInput.value = "";
    }

    async function addEsencia() {
      var check = ensureConfigForTier(cfg, "esencia", i18n);
      if (!check.ok) { alert(check.reason); return; }
      resetQty();
      var props = ensureUid(serializeFormProperties(form));
      disableSubmit(form, true);
      try {
        await addItemsToCart({
          items: [{
            id: Number(jewelVariantId),
            quantity: 1,
            properties: Object.assign({}, props, {
              _av_tier: "esencia",
              _av_line_id: makeLineId(),
            }),
          }],
        });
        await showPopupAfterAdd(
          productTitle,
          t(i18n, "plan_esencia_label", null, "Plan Esencia (gratis)"),
          i18n,
          cartUrl,
          priceText,
          props._av_uid
        );
      } catch (err) {
        console.error("av-tier.js:", err);
        alert(t(i18n, "err_add_failed", null, "Add to cart failed."));
      } finally {
        disableSubmit(form, false);
        clearUid();
      }
    }

    async function addInfinita() {
      var check = ensureConfigForTier(cfg, "infinita", i18n);
      if (!check.ok) { alert(check.reason); return; }
      resetQty();
      var props = ensureUid(serializeFormProperties(form));
      var avUid = props._av_uid ? String(props._av_uid) : "";
      var upgrade = cfg.infinita;
      disableSubmit(form, true);
      try {
        // _av_had_own_upgrade flags this jewel as "originally paid as Infinita" so the
        // unlink action knows to restore Infinita for it (and NOT for jewels that
        // were Esencia-by-origin but joined an Infinita group via linking).
        await addItemsToCart({
          items: [
            {
              id: Number(jewelVariantId),
              quantity: 1,
              properties: Object.assign({}, props, {
                _av_tier: "infinita",
                _av_had_own_upgrade: "true",
                _av_line_id: makeLineId(),
              }),
            },
            {
              id: Number(upgrade.variant_id),
              quantity: 1,
              properties: {
                _av_tier: "infinita",
                _av_uid: avUid,
                _av_upgrade: "true",
                _av_line_id: makeLineId(),
              },
            },
          ],
        });
        await showPopupAfterAdd(
          productTitle,
          t(i18n, "plan_infinita_label", null, "Plan Infinita"),
          i18n,
          cartUrl,
          priceText,
          avUid
        );
      } catch (err) {
        console.error("av-tier.js:", err);
        alert(t(i18n, "err_add_failed", null, "Add to cart failed."));
      } finally {
        disableSubmit(form, false);
        clearUid();
      }
    }

    if (tier === "esencia") {
      // Offer the Infinita upsell before adding Esencia — but only if a valid
      // upgrade variant exists AND the shopper hasn't already resolved it once
      // this session (so buying a linked set doesn't nag on every jewel).
      var infinitaOk = ensureConfigForTier(cfg, "infinita", i18n).ok;
      if (infinitaOk && !upsellResolved()) {
        showEsenciaUpsell({
          i18n: i18n,
          onUpgrade: function () { markUpsellResolved(); addInfinita(); },   // "Add Infinita instead"
          onContinue: function () { markUpsellResolved(); addEsencia(); },   // "No thanks, continue with Esencia"
          onClose: null,            // X / backdrop / Esc → cancel, nothing added, popup can reappear
        });
      } else {
        addEsencia();
      }
    } else {
      addInfinita();
    }
  }

  /* ---- init ---- */

  function init() {
    $all(document, 'form[data-type="add-to-cart-form"]').forEach(function (form) {
      onTierPickerInit(form);
    });

    document.addEventListener("shopify:section:load", function (evt) {
      var section = evt.target;
      if (!section) return;
      $all(section, 'form[data-type="add-to-cart-form"]').forEach(function (form) {
        onTierPickerInit(form);
      });
    });

    document.addEventListener("submit", onSubmit, true);
  }

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", init);
  else init();
})();
